import React, { useState, useEffect } from "react";
import { User } from "../../../../../services/eventService";

interface RegisteredUsersModalProps {
  show: boolean;
  onClose: () => void;
  registeredUsers: User[];
  registrations: any[];
  handleDownloadUserMail: (userId: string) => void;
  setShowRegisterUserModal: (show: boolean) => void;
  handleUnregisterUser?: (userId: string) => void;
}

// User list item component
const UserListItem: React.FC<{
  user: User;
  registration?: any;
  onDownloadMail: (userId: string) => void;
  onUnregister?: (userId: string) => void;
}> = ({ user, registration, onDownloadMail, onUnregister }) => (
  <li className="list-group-item d-flex align-items-center">
    {/* Profile Image */}
    <img
      src={user.picture 
        ? `data:image/png;base64,${user.picture}` 
        : "/static/images/user-profile-icon.png"
      }
      className="rounded-circle me-3"
      style={{ width: "50px", height: "50px" }}
      alt={user.picture ? "User Image" : "Default User Image"}
    />

    {/* User Info */}
    <div className="flex-grow-1">
      <div className="fw-bold">
        {user.firstname} {user.lastname}
      </div>
      <small className="text-muted">{user.mail}</small>
      {user.firm && (
        <small className="text-muted d-block">
          ({user.firm.abbrev})
        </small>
      )}
    </div>

    {/* Action Buttons */}
    <div className="d-flex gap-2">
      {/* Download Mail button */}
      <button
        className="btn btn-success btn-sm flex-shrink-0"
        style={{ minWidth: '140px' }}
        onClick={() => onDownloadMail(user.User_ID)}
        title="Download mail for this user"
      >
        <i className="bi bi-envelope-arrow-down me-1"></i>
        Download Mail
      </button>
      
      {/* Unregister button (optional) */}
      {onUnregister && (
        <button
          className="btn btn-danger btn-sm flex-shrink-0"
          style={{ minWidth: '120px' }}
          onClick={() => onUnregister(user.User_ID)}
          title="Remove user from event"
        >
          <i className="bi bi-person-dash-fill me-1"></i>
          Unregister
        </button>
      )}
    </div>
  </li>
);

// Register New User button component
const RegisterNewUserButton: React.FC<{
  onClick: () => void;
}> = ({ onClick }) => (
  <button
    className="btn btn-outline-success btn-sm d-flex align-items-center justify-content-start"
    onClick={onClick}
    style={{ padding: "0.5rem 0.75rem", fontSize: "0.85rem" }}
  >
    <i
      className="bi bi-person-plus me-2"
      style={{ fontSize: "0.9rem" }}
    ></i>
    <span>Register New User</span>
  </button>
);

const RegisteredUsersModal: React.FC<RegisteredUsersModalProps> = ({
  show,
  onClose,
  registeredUsers,
  registrations,
  handleDownloadUserMail,
  setShowRegisterUserModal,
  handleUnregisterUser
}) => {
  // Local state to track the current registered users
  const [localRegisteredUsers, setLocalRegisteredUsers] = useState<User[]>(registeredUsers);
  const [localRegistrations, setLocalRegistrations] = useState<any[]>(registrations);

  // Update local state when props change or modal is shown
  useEffect(() => {
    if (show) {
      setLocalRegisteredUsers(registeredUsers);
      setLocalRegistrations(registrations);
    }
  }, [registeredUsers, registrations, show]);

  // Wrapped handler for unregister to update local state immediately
  const handleLocalUnregister = (userId: string) => {
    if (handleUnregisterUser) {
      // Call the original handler
      handleUnregisterUser(userId);
      
      // Update local state immediately for better UX
      setLocalRegisteredUsers(prev => prev.filter(user => user.User_ID !== userId));
      setLocalRegistrations(prev => prev.filter(reg => reg.User_ID !== userId));
    }
  };

  if (!show) return null;

  return (
    <div className="modal show d-block" tabIndex={-1}>
      <div className="modal-dialog modal-lg">
        <div className="modal-content">
          {/* Modal Header */}
          <div className="modal-header">
            <h5 className="modal-title">
              <i className="bi bi-people-fill me-2"></i>
              Registered Users for Event
            </h5>
            <button
              type="button"
              className="btn-close"
              onClick={onClose}
            ></button>
          </div>

          {/* Modal Body */}
          <div className="modal-body">
            {localRegisteredUsers.length > 0 ? (
              <div className="mb-2">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <span className="badge bg-primary">
                    {localRegisteredUsers.length} users registered
                  </span>
                </div>
                <ul className="list-group">
                  {localRegisteredUsers.map((user) => {
                    const registration = localRegistrations.find(
                      (reg) => reg.User_ID === user.User_ID
                    );
                    return (
                      <UserListItem
                        key={user.User_ID}
                        user={user}
                        registration={registration}
                        onDownloadMail={handleDownloadUserMail}
                        onUnregister={handleLocalUnregister}
                      />
                    );
                  })}
                </ul>
              </div>
            ) : (
              <div className="text-center py-5">
                <i className="bi bi-people fs-1 text-muted"></i>
                <p className="text-muted mt-2">No users registered for this event yet.</p>
              </div>
            )}
          </div>

          {/* Modal Footer */}
          <div className="modal-footer">
            <RegisterNewUserButton onClick={() => setShowRegisterUserModal(true)} />
            <button type="button" className="btn btn-primary" onClick={onClose}>
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegisteredUsersModal;
